/**
 * „Tydzień wzorcowy" (T-B2, T-B2e) — seven days, each a switch, its windows and two small acts:
 * „+" adds a window and opens the day, the copy mark copies the day onto others. One day at a time
 * opens in place (T-B2e) as fields, with the day's one refusal under them; „Gotowe" closes it.
 *
 * Nothing here writes until „Zapisz": the week is sent whole (`useWeekDraft`), so the day switches
 * are named as checkboxes (staged), not switches. „Zapisz" stays focusable while it cannot send and
 * says why, in words beside it. The weekly total reads the draft, so it moves as the trainer edits.
 *
 * A closed day whose windows fight says so in a line under its row, and its button names the
 * hours and the fight — the warning edge on the chips is never the only sign. Focus follows the
 * edit: opening a day lands on its first field (or „Dodaj okno" when it has none), „Gotowe" returns
 * to that day's windows. While a save is out the days are frozen (a disabled fieldset): an edit
 * then would be in neither the week sent nor the week kept.
 *
 * While the hours are read the card stands in its own loaded shape — the seven rows, named — with
 * bars where the switches, the windows and the total will be (`waiting`), so nothing moves when
 * they land.
 */

import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog } from '~/shared/ui/ConfirmDialog';
import { ScreenHeaderPlus } from '~/shared/ui/ScreenHeader';
import ember from '~/shared/ui/emberButton.module.css';
import skeleton from '~/shared/ui/skeleton.module.css';
import { CopyGlyph } from '~/shared/ui/FormGlyphs';
import { Toggle } from '~/shared/ui/Toggle';
import { WarningGlyph } from '~/shared/ui/WarningGlyph';
import form from '~/shared/ui/formDialog.module.css';

import { weekHoursLabel } from '../model/policySettings';
import { windowProblemText, windowSpan } from '../model/scheduleWords';
import {
  dayToggled,
  weeklyMinutes,
  windowAdded,
  windowRemoved,
  windowRetimed,
  type Weekday,
} from '../model/weekPattern';
import type { WeekDraft } from '../lib/useWeekDraft';
import { WindowFields } from './WindowFields';
import frame from './bookingTab.module.css';
import styles from './ScheduleTab.module.css';

interface WeekCardProps {
  readonly draft: WeekDraft;
  readonly openDay: Weekday | null;
  readonly onOpenDay: (weekday: Weekday | null) => void;
  readonly onCopyDay: (weekday: Weekday) => void;
  readonly onCopyMonday: () => void;
  /** The read is still out: draw the shape, bars where the values go (drawn once `bars`). */
  readonly waiting?: { readonly bars: boolean };
}

export function WeekCard({
  draft,
  openDay,
  onOpenDay,
  onCopyDay,
  onCopyMonday,
  waiting,
}: WeekCardProps) {
  const { t, i18n } = useTranslation();
  const blockedId = useId();
  const dayName = (weekday: Weekday) => t(`web:weekdays.full.${String(weekday)}`);
  const card = useRef<HTMLElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  // „Wczytaj tamten tydzień" asks first: it throws the trainer's edits away. Once confirmed, focus
  // lands on the card's heading — the button it was asked from is gone with the drift.
  const [askingReload, setAskingReload] = useState(false);
  const [reloaded, setReloaded] = useState(false);
  // Where focus goes once the edit it follows has rendered: a selector inside this card.
  const focusNext = useRef<readonly string[] | null>(null);
  useEffect(() => {
    const wanted = focusNext.current;
    if (wanted === null) return;
    focusNext.current = null;
    for (const selector of wanted) {
      const target = card.current?.querySelector<HTMLElement>(selector);
      if (target != null) {
        target.focus();
        return;
      }
    }
  });
  const bar = waiting?.bars === true ? String(skeleton.bar) : '';
  const openDayFocused = (weekday: Weekday) => {
    focusNext.current = ['[data-window-scope] input', '[data-window-scope] [data-add-window]'];
    onOpenDay(weekday);
  };

  return (
    <section
      ref={card}
      className={frame.card}
      aria-labelledby="booking-week-title"
      aria-busy={draft.saving}
      aria-hidden={waiting === undefined ? undefined : true}
      inert={waiting !== undefined}
    >
      <div className={styles.cardHead}>
        <h2 ref={heading} id="booking-week-title" className={frame.cardTitle} tabIndex={-1}>
          {t('web:booking.trainer.schedule.weekHead')}
        </h2>
        <span className={waiting === undefined ? form.note : `${String(styles.waitNote)} ${bar}`}>
          {t('web:booking.schedule.weekTotal', {
            hours: weekHoursLabel(weeklyMinutes(draft.pattern), i18n.language),
          })}
        </span>
      </div>
      <p className={styles.cardIntro}>{t('web:booking.schedule.weekIntro')}</p>

      <fieldset className={styles.frozen} disabled={draft.saving}>
        <ul className={styles.days}>
          {draft.pattern.days.map((day) => {
            const verdict = draft.verdict.days[day.weekday];
            const on = day.windows.length > 0;
            const toggle =
              waiting === undefined ? (
                <Toggle
                  checked={on}
                  label={t('web:booking.schedule.dayWorks', { day: dayName(day.weekday) })}
                  onChange={() => {
                    draft.edit((pattern) => dayToggled(pattern, day.weekday));
                  }}
                />
              ) : (
                <span className={`${String(styles.toggleBar)} ${bar}`} />
              );
            if (openDay === day.weekday) {
              return (
                <li key={day.weekday} className={styles.dayOpen} data-window-scope>
                  <div className={styles.dayOpenHead}>
                    {toggle}
                    <span className={styles.dayOpenName}>{dayName(day.weekday)}</span>
                    <button
                      type="button"
                      className={styles.tinyGhost}
                      onClick={() => {
                        focusNext.current = [`[data-day-windows="${String(day.weekday)}"]`];
                        onOpenDay(null);
                      }}
                    >
                      {t('web:booking.schedule.done')}
                    </button>
                  </div>
                  <WindowFields
                    windows={day.windows}
                    bad={verdict.bad}
                    problem={verdict.problem}
                    onRetime={(id, change) => {
                      draft.edit((pattern) => windowRetimed(pattern, day.weekday, id, change));
                    }}
                    onRemove={(id) => {
                      draft.edit((pattern) => windowRemoved(pattern, day.weekday, id));
                    }}
                  />
                  <div className={styles.dayOpenActs}>
                    <button
                      type="button"
                      className={styles.tinyCard}
                      data-add-window
                      onClick={() => {
                        draft.edit((pattern) => windowAdded(pattern, day.weekday));
                      }}
                    >
                      <ScreenHeaderPlus className={styles.tinyGlyph} />
                      {t('web:booking.trainer.day.addWindow')}
                    </button>
                    <button
                      type="button"
                      className={styles.tinyGhost}
                      onClick={() => {
                        onCopyDay(day.weekday);
                      }}
                    >
                      <CopyGlyph className={styles.tinyGlyph} />
                      {t('web:booking.schedule.copyTo')}
                    </button>
                  </div>
                </li>
              );
            }
            const problem = verdict.problem === null ? null : windowProblemText(verdict.problem, t);
            const hours = on
              ? day.windows.map((window) => windowSpan(window)).join(', ')
              : t('web:booking.trainer.schedule.free');
            return (
              <li key={day.weekday} className={styles.day}>
                {toggle}
                <span className={styles.dayName}>{dayName(day.weekday)}</span>
                <button
                  type="button"
                  className={styles.dayWindows}
                  data-day-windows={day.weekday}
                  aria-label={[
                    t('web:booking.schedule.editDay', { day: dayName(day.weekday) }),
                    hours,
                    problem,
                  ]
                    .filter((part) => part !== null)
                    .join('. ')}
                  onClick={() => {
                    openDayFocused(day.weekday);
                  }}
                >
                  {waiting !== undefined ? (
                    <span className={`${String(styles.chipBar)} ${bar}`} />
                  ) : on ? (
                    day.windows.map((window) => (
                      <span
                        key={window.id}
                        className={`${String(styles.windowChip)} ${verdict.bad.includes(window.id) ? String(styles.windowChipBad) : ''}`}
                      >
                        {windowSpan(window)}
                      </span>
                    ))
                  ) : (
                    <span className={styles.free}>{t('web:booking.trainer.schedule.free')}</span>
                  )}
                </button>
                <button
                  type="button"
                  className={styles.squareButton}
                  aria-label={t('web:booking.schedule.addWindowTo', { day: dayName(day.weekday) })}
                  onClick={() => {
                    draft.edit((pattern) => windowAdded(pattern, day.weekday));
                    openDayFocused(day.weekday);
                  }}
                >
                  <ScreenHeaderPlus className={styles.squareGlyph} />
                </button>
                <button
                  type="button"
                  className={styles.squareButton}
                  aria-label={t('web:booking.schedule.copyDay', {
                    day: t(`web:weekdays.accusative.${String(day.weekday)}`),
                  })}
                  onClick={() => {
                    onCopyDay(day.weekday);
                  }}
                >
                  <CopyGlyph className={styles.squareGlyph} />
                </button>
                {problem === null ? null : (
                  <p className={styles.dayWarn}>
                    <WarningGlyph className={styles.warnGlyph} />
                    <span>{problem}</span>
                  </p>
                )}
              </li>
            );
          })}
        </ul>

        <button
          type="button"
          className={`${String(styles.tinyGhost)} ${String(styles.copyWeek)}`}
          onClick={onCopyMonday}
        >
          <CopyGlyph className={styles.tinyGlyph} />
          {t('web:booking.trainer.schedule.copyMonday')}
        </button>
      </fieldset>

      {draft.drifted ? (
        <div className={styles.drift} role="alert">
          <span className={styles.driftText}>
            <span>{t('web:booking.schedule.drift.line')}</span>
            <span className={styles.driftWeek}>{draft.elsewhere}</span>
          </span>
          <span className={styles.driftActs}>
            <button
              type="button"
              className={styles.tinyGhost}
              onClick={() => {
                setReloaded(false);
                setAskingReload(true);
              }}
            >
              {t('web:booking.schedule.drift.reload')}
            </button>
            <button
              type="button"
              className={styles.tinyGhost}
              onClick={() => {
                focusNext.current = ['[data-save]'];
                draft.overwrite();
              }}
            >
              {t('web:booking.schedule.drift.overwrite')}
            </button>
          </span>
        </div>
      ) : null}
      <ConfirmDialog
        prompt={
          // Only while the week still drifted: a read that brought the two weeks back together
          // leaves nothing to load, and the question closes with it.
          askingReload && draft.drifted
            ? {
                title: t('web:booking.schedule.drift.reloadTitle'),
                description: t('web:booking.schedule.drift.reloadBody'),
                confirmLabel: t('web:booking.schedule.drift.reloadConfirm'),
                cancelLabel: t('web:booking.schedule.drift.reloadCancel'),
                destructiveConfirm: true,
              }
            : null
        }
        finalFocus={reloaded ? heading : undefined}
        onConfirm={() => {
          setReloaded(true);
          setAskingReload(false);
          draft.reload();
        }}
        onCancel={() => {
          setAskingReload(false);
        }}
      />

      {draft.refusal === null ? null : (
        <p className={form.refusal} role="alert">
          {draft.refusal}
        </p>
      )}

      <div className={styles.cardFoot}>
        {/* Why „Zapisz" holds stands beside it — except while the week drifted, where the line
            above already says it and the reason stays only in the button's description. */}
        {draft.saveBlocked === null || !draft.dirty || draft.drifted ? (
          <span className={form.note}>{t('web:booking.schedule.note')}</span>
        ) : (
          <span id={blockedId} className={styles.saveReason} aria-live="polite">
            {draft.saveBlocked}
          </span>
        )}
        {draft.saveBlocked !== null && (!draft.dirty || draft.drifted) ? (
          <span id={blockedId} hidden>
            {draft.saveBlocked}
          </span>
        ) : null}
        <button
          type="button"
          className={`${String(ember.ember)} ${String(styles.save)}`}
          data-save
          aria-disabled={draft.saveBlocked !== null || draft.saving ? true : undefined}
          aria-describedby={draft.saveBlocked === null ? undefined : blockedId}
          aria-busy={draft.saving}
          onClick={draft.save}
        >
          {draft.saving ? t('web:booking.schedule.saving') : t('web:booking.schedule.save')}
        </button>
      </div>
    </section>
  );
}
